import { FC } from 'react';
import { IonButton, IonContent, IonModal } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';

const useStyles = createUseStyles({
    ionContent: {
        // 0px, not unitless 0: these feed Ionic's inner-scroll padding calc(), which a unitless 0 invalidates
        '--padding-top': '0px',
        '--padding-bottom': '0px',
        height: '100%',
        display: 'flex'
    },

    content: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px 32px 24px',
        textAlign: 'center'
    },

    checkIcon: {
        width: 64,
        height: 64,
        backgroundColor: '#4CAF50',
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 24,
        fontSize: 32,
        color: 'white'
    },

    beaAvatar: {
        width: 100,
        height: 100,
        borderRadius: '50%',
        marginBottom: 20
    },

    title: {
        fontSize: 18,
        fontWeight: 600,
        color: 'var(--ion-color-dark)',
        marginBottom: 16
    },

    introTitle: {
        fontSize: 22,
        fontWeight: 700,
        color: 'var(--ion-color-dark)',
        marginBottom: 8
    },

    message: {
        fontSize: 14,
        marginTop: 12,
        marginBottom: 40,
        lineHeight: 1.4,
        maxWidth: 280
    },

    introMessage: {
        fontSize: 15,
        color: '#666',
        lineHeight: 1.5,
        maxWidth: 300,
        marginTop: 8,
        marginBottom: 16
    },

    ellipsisContainer: {
        marginBottom: 48,
        marginTop: 16,
        display: 'flex',
        gap: 4,
        justifyContent: 'center'
    },

    dot: {
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: 'var(--ion-color-primary)',
        animation: '$blink 1.4s infinite both',

        '&:nth-child(1)': {
            animationDelay: '0s'
        },
        '&:nth-child(2)': {
            animationDelay: '0.2s'
        },
        '&:nth-child(3)': {
            animationDelay: '0.4s'
        }
    },

    '@keyframes blink': {
        '0%': {
            opacity: 0.2,
            transform: 'scale(0.8)'
        },
        '20%': {
            opacity: 1,
            transform: 'scale(1)'
        },
        '100%': {
            opacity: 0.2,
            transform: 'scale(0.8)'
        }
    },

    button: {
        '--padding-start': '32px',
        '--padding-end': '32px',
        '--padding-top': '12px',
        '--padding-bottom': '12px',
        fontSize: 16,
        '--border-radius': '8px',
        width: '100%',
        maxWidth: 280
    }
});

interface IRecordingSuccessModalProps {
    isOpen: boolean;
    title: string;
    message: string;
    isIntro?: boolean;
    buttonText?: string;
    trackingType: 'auto-saved' | 'user-saved';
    trackingTitle: string;
    trackingMessage: string;
    trackingIsOffline: boolean;
    onDismiss: () => void;
}

export const RecordingSuccessModal: FC<IRecordingSuccessModalProps> = ({
    isOpen,
    title,
    message,
    isIntro,
    buttonText,
    trackingType,
    trackingTitle,
    trackingMessage,
    trackingIsOffline,
    onDismiss
}) => {
    const classes = useStyles();
    const mixpanel = useInstance(MixPanelEventEmitter);

    const handleDismiss = () => {
        mixpanel.track('Recording Success Acknowledged', () => ({
            'type': trackingType,
            'title': trackingTitle,
            'message': trackingMessage,
            'isOffline': trackingIsOffline
        }));
        onDismiss();
    };

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={onDismiss}
            backdropDismiss={false}
            data-id="recording-success-modal"
        >
            <IonContent className={classes.ionContent}>
                <div className={classes.content}>
                    {isIntro ? (
                        <img
                            src="/assets/images/help/helper-bea-large.png"
                            alt="Bea"
                            className={classes.beaAvatar}
                        />
                    ) : (
                        <div className={classes.checkIcon}>
                            ✓
                        </div>
                    )}

                    <div className={isIntro ? classes.introTitle : classes.title}>
                        {title}
                    </div>

                    <div className={isIntro ? classes.introMessage : classes.message}>
                        {message}
                    </div>

                    <div className={classes.ellipsisContainer}>
                        <div className={classes.dot}></div>
                        <div className={classes.dot}></div>
                        <div className={classes.dot}></div>
                    </div>

                    <IonButton
                        expand="block"
                        className={classes.button}
                        onClick={handleDismiss}
                        data-id="got-it-button"
                    >
                        {buttonText || 'Got It!'}
                    </IonButton>
                </div>
            </IonContent>
        </IonModal>
    );
};
